import { InputNumber } from "antd";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import React, { useState } from "react";
import { Amount } from "../../../types/amount";
import { css } from "@emotion/react";
import { GlobalStore } from "../../../store/global/GlobalStore";

export interface CurrencyInputProps {
  value: string;
  onChange: (value: string) => void;
  formatter: (value: string) => string;
  isVisible?: boolean;
}
export const CurrencyInputNew = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onChange, formatter, isVisible = true }, ref) => {
    const prevIsVisible = useRef(isVisible);
    useEffect(() => {
      if (isVisible && !prevIsVisible.current) {
        setInputValue(value);
      }
    }, [isVisible, value]);

    const PLACEHOLDER = "0";
    const theme = GlobalStore.useState(s => s.theme);
    const formatValue = useCallback(
      (
        value: string | undefined,
        info: {
          userTyping: boolean;
          input: string;
        }
      ) => {
        console.log(`Running formatter`);
        if (info.userTyping && info.input) {
          return info.input;
        }
        const res = value ?? PLACEHOLDER;
        console.log(`formatter res:`, res);
        return res;
      },
      []
    );
    const [inputValue, setInputValue] = useState<string>(value);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!/[0-9.]/.test(event.key)) {
        event.preventDefault();
      } else if (event.currentTarget.value.includes(".") && event.key === ".") {
        // Prevent multiple decimal separators
        event.preventDefault();
      }
    };

    const handleBlur = () => {
      const formattedValue = formatter(inputValue);
      setInputValue(formattedValue);
      onChange(formattedValue); // Update external state on blur with formatted value
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.focus();
      e.target.setSelectionRange(0, 0);
      e.target.scrollLeft = 0;
      setInputValue(value === "0" ? "" : value); // Revert to full precision value on focus
    };
    useEffect(() => {
      prevIsVisible.current = isVisible;
    }, [isVisible]);
    return (
      <InputNumber<string>
        rootClassName={"currency-input"}
        ref={ref}
        css={css`
          opacity: ${isVisible ? 1 : 0};
          :focus {
            outline: none;
          }
          transition: none !important;

          input {
            font: ${theme.Desktop.Title.XL} !important;
            color: white !important;
            text-align: right !important;
            text-overflow: ellipsis !important;
            font-weight: 500 !important;
            animation-duration: 0s !important;
            transition: none !important;
            padding: 0 !important;
          }

          input::placeholder {
            color: ${theme.font.secondary.default} !important;
          }

          background: none;
          border: 0;
          text-align: right;
          color: ${theme.font.primary.default};
          font-size: 28px;
          font-style: normal;
          font-weight: 500;
          line-height: 120%;
          width: 100%;
        `}
        autoFocus
        controls={false}
        decimalSeparator="."
        formatter={formatValue}
        parser={(displayValue: string | undefined) => {
          console.log(`Running parser`);
          if (displayValue === undefined) return "";
          try {
            //Try to parse the value - if it fails, return the display value
            new Amount(displayValue ?? "");
            return displayValue;
          } catch (error) {
            console.log(`Error returning`, displayValue);
            return displayValue;
          }
        }}
        value={inputValue}
        onKeyPress={handleKeyPress}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={PLACEHOLDER}
        defaultValue="0.0"
        min="0.0"
        max="1000000000000000"
        step="0.00000000000001"
        onChange={value => {
          if (value) {
            setInputValue(value);
            onChange(value);
          }
        }}
        stringMode
      />
    );
  }
);
